import { useEffect, useState } from 'react';
import type { UpdateSmsSettingsInput } from '@klaro7/validators';
import { Badge, Button, Input, Spinner } from '@klaro7/ui';

import { usePinGate } from '@/components/PinGate';
import {
  useSmsSettings,
  useUpdateSmsSettings,
  useSendTestSms,
} from '../hooks/useServicesSettings';

function extractErrorDetail(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const resp = err as {
      response?: {
        data?: { error?: { message?: string }; message?: string };
        status?: number;
        statusText?: string;
      };
    };
    const apiMsg = resp.response?.data?.error?.message || resp.response?.data?.message;
    const status = resp.response?.status;
    if (apiMsg) return `[${status}] ${apiMsg}`;
    if (status) return `HTTP ${status}: ${resp.response?.statusText || 'Errore sconosciuto'}`;
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

/**
 * OVH SMS configuration. Super-admin only — the parent gates rendering.
 *
 * applicationSecret + consumerKey arrive masked from the server; each is included
 * in the PUT payload only when the admin types a new value (mirrors Stripe), so a
 * stored secret is never overwritten with its mask.
 *
 * The "Send test" sends a REAL SMS via the alphanumeric sender. The sender must be
 * validated in the OVH "Senders" section or the send fails.
 */
export function SmsSettingsSection() {
  const { requirePin, pinDialog } = usePinGate();
  const { data: settings, isLoading } = useSmsSettings(true);
  const updateMutation = useUpdateSmsSettings();
  const testMutation = useSendTestSms();

  const [applicationKey, setApplicationKey] = useState('');
  const [applicationSecret, setApplicationSecret] = useState('');
  const [consumerKey, setConsumerKey] = useState('');
  const [serviceName, setServiceName] = useState('');
  const [sender, setSender] = useState('WSSERVICESE');
  const [enabled, setEnabled] = useState(false);
  const [savedSecret, setSavedSecret] = useState('');
  const [savedConsumerKey, setSavedConsumerKey] = useState('');

  const [testTo, setTestTo] = useState('');
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [testResult, setTestResult] = useState<{ ok: boolean; detail: string } | null>(null);

  useEffect(() => {
    if (settings) {
      setApplicationKey(settings.applicationKey ?? '');
      setApplicationSecret(settings.applicationSecret ?? '');
      setConsumerKey(settings.consumerKey ?? '');
      setServiceName(settings.serviceName ?? 'sms-mv15828-1');
      setSender(settings.sender ?? 'WSSERVICESE');
      setEnabled(settings.enabled ?? false);
      setSavedSecret(settings.applicationSecret ?? '');
      setSavedConsumerKey(settings.consumerKey ?? '');
    }
  }, [settings]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), toast.type === 'error' ? 15000 : 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const senderInvalid = sender.length > 11 || !/[A-Za-z]/.test(sender);

  async function handleSave() {
    if (senderInvalid) {
      setToast({ type: 'error', message: 'Mittente non valido: max 11 caratteri, almeno una lettera.' });
      return;
    }
    const payload: UpdateSmsSettingsInput = { applicationKey, serviceName, sender, enabled };
    if (applicationSecret !== savedSecret) {
      payload.applicationSecret = applicationSecret;
    }
    if (consumerKey !== savedConsumerKey) {
      payload.consumerKey = consumerKey;
    }
    try {
      await updateMutation.mutateAsync(payload);
      setToast({ type: 'success', message: 'Configurazione SMS salvata con successo' });
    } catch (err) {
      setToast({ type: 'error', message: `Errore nel salvataggio SMS: ${extractErrorDetail(err)}` });
    }
  }

  async function handleTest() {
    setTestResult(null);
    try {
      const result = await testMutation.mutateAsync({ to: testTo.trim() });
      const credits = result.creditsLeft != null ? ` (crediti: ${result.creditsLeft})` : '';
      setTestResult({ ok: true, detail: `ID ${result.sid} — ${result.status}${credits}` });
    } catch (err) {
      setTestResult({ ok: false, detail: extractErrorDetail(err) });
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  const testToValid = /^\+[1-9]\d{6,14}$/.test(testTo.trim());

  return (
    <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
      <div className="flex items-center justify-between border-b border-neutral-100 bg-neutral-50 px-5 py-3.5">
        <div className="flex items-center gap-3">
          <svg className="h-5 w-5 text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
          </svg>
          <div>
            <h3 className="text-sm font-semibold text-neutral-700">SMS (OVHcloud)</h3>
            <p className="text-xs text-neutral-600">Invio SMS con mittente alfanumerico (solo super admin)</p>
          </div>
        </div>
        <Badge variant={enabled ? 'success' : 'default'}>
          {enabled ? 'Attivo' : 'Disattivato'}
        </Badge>
      </div>

      <div className="space-y-4 p-5">
        {toast && (
          <div
            className={`rounded-lg border px-4 py-2.5 text-sm font-medium ${
              toast.type === 'success'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : 'border-red-200 bg-red-50 text-red-700'
            }`}
          >
            {toast.message}
          </div>
        )}

        <Input
          label="Application Key (AK)"
          value={applicationKey}
          onChange={(e) => setApplicationKey(e.target.value)}
          placeholder="xxxxxxxxxxxxxxxx"
          helperText="Identificatore applicazione OVH, salvato in chiaro."
        />

        <Input
          label="Application Secret (AS)"
          type="password"
          value={applicationSecret}
          onChange={(e) => setApplicationSecret(e.target.value)}
          placeholder="••••••••••••••••"
          helperText="Segreto applicazione, cifrato. Lascia invariato il valore mascherato per non sovrascriverlo."
        />

        <Input
          label="Consumer Key (CK)"
          type="password"
          value={consumerKey}
          onChange={(e) => setConsumerKey(e.target.value)}
          placeholder="••••••••••••••••"
          helperText="Token utente autorizzato, cifrato. Diritti richiesti: GET/POST/PUT/DELETE /sms*."
        />

        <Input
          label="Service Name"
          value={serviceName}
          onChange={(e) => setServiceName(e.target.value)}
          placeholder="sms-mv15828-1"
          helperText="Identificativo account SMS OVH (dall'URL del manager)."
        />

        <Input
          label="Mittente (sender)"
          value={sender}
          onChange={(e) => setSender(e.target.value)}
          placeholder="WSSERVICESE"
          error={senderInvalid ? 'Max 11 caratteri, almeno una lettera' : undefined}
          helperText="Alfanumerico, validato nella sezione Senders OVH. Unidirezionale: nessuna risposta."
        />

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-4 w-4 rounded border-neutral-300 text-brand-teal focus:ring-brand-teal"
          />
          Abilitato
        </label>

        <div className="rounded-lg border border-neutral-100 bg-neutral-50 p-4">
          <p className="mb-2 text-xs font-medium text-neutral-600">Invia un SMS di test</p>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Input
                label="Numero destinatario (E.164)"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="+39XXXXXXXXXX"
              />
            </div>
            <Button
              variant="outline"
              size="md"
              onClick={handleTest}
              loading={testMutation.isPending}
              disabled={!testToValid}
              title={testToValid ? 'Invia SMS di test' : 'Inserisci un numero E.164 valido'}
            >
              Invia test
            </Button>
          </div>
          {testResult && (
            <div
              className={`mt-3 flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium ${
                testResult.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
              }`}
            >
              {testResult.ok ? (
                <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
              <span>{testResult.ok ? `Inviato — ${testResult.detail}` : `KO — ${testResult.detail}`}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end border-t border-neutral-100 pt-4">
          {pinDialog}
          <Button onClick={() => requirePin(handleSave)} loading={updateMutation.isPending} disabled={senderInvalid}>
            Salva SMS
          </Button>
        </div>
      </div>
    </div>
  );
}
